import { Loader2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { AdminApi } from "@/api";
import { CauseLoopSummary } from "@/components/test-cases/CauseLoopSummary";
import { CriteriaBlock } from "@/components/test-cases/CriteriaBlock";
import { EvaluationStatusBadge } from "@/components/test-cases/EvaluationStatusBadge";
import {
  domainLabel,
  isProposedDomain,
} from "@/components/test-cases/allTestCasesSearch";
import {
  DROPPED_LABEL,
  droppedReasonText,
  isDropped,
  reactivateDroppedReason,
} from "@/components/test-cases/dropState";
import { testCaseErrorText, testCaseGate, traceEnvironment } from "@/components/test-cases/prodCase";
import {
  feedbackCountText,
  isSourceDeleted,
  isSourceUnavailable,
  SOURCE_DELETED_LABEL,
  SOURCE_DELETED_TEXT,
  SOURCE_UNAVAILABLE_LABEL,
  sourceUnavailableText,
} from "@/components/test-cases/sourceStatus";
import type { TestCaseSource } from "@/components/test-cases/testCasesSource";
import { Badge, Banner, Button, Card, IconButton, Skeleton } from "@/components/ui";
import type { TestCaseAnswerFeedback, TestCaseDetail, TestCaseWarning, TestExecutionSummary } from "@/types";
import type { Candidate } from "@/types/issuePipeline";
import { compactTime } from "@/utils/format";
import { sessionTypeLabel } from "@/utils/sessionType";

/**
 * The right half of All Test Cases (fe/TEST_CASES_ALL.md §6).
 *
 * Six blocks, and the order is the reading order: what was asked → what went
 * wrong → how it is classified → what kind of case it is → what identifies it
 * → what can be done with it.
 *
 * It fetches its own detail rather than taking the list row, because the list
 * row has no trigger body, no per-👎 breakdown and no evidence.
 */
export function TestCaseDrawer({
  openId,
  source,
  onClose,
  onOpenOriginal,
  onOpenResult,
  onUpdated,
  originCandidates,
  loopApi,
}: {
  openId: string;
  source: TestCaseSource;
  onClose: () => void;
  onOpenOriginal: (detail: TestCaseDetail) => void;
  /** This TC's row in the Test Result Matrix. */
  onOpenResult: (detail: TestCaseDetail) => void;
  /**
   * The PATCH response, so the list can adopt it. Retiring is a change, not
   * a navigation — the row has to dim where it sits, and refetching the page
   * to learn that would lose the scroll position and the loaded pages.
   */
  onUpdated: (detail: TestCaseDetail) => void;
  /** Issue candidates promoted into, or linked to, this TC. */
  originCandidates?: (testCaseId: string) => Promise<Candidate[]>;
  /** The cause loop's state of this TC (LOOPFLOW E-14); without it the block is left out. */
  loopApi?: AdminApi;
}) {
  const [detail, setDetail] = useState<TestCaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setDetail(await source.detail(openId));
    } catch (err) {
      setDetail(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [openId, source]);

  useEffect(() => {
    void load();
  }, [load]);

  // After a new criteria version: the same fetch, but the drawer stays drawn
  // so the block can say what happened.
  // Says whether the read worked; the block says what that means (VERIFY-86 #2).
  const refresh = useCallback(async () => {
    try {
      setDetail(await source.detail(openId));
      return true;
    } catch {
      return false;
    }
  }, [openId, source]);

  const sourceUnavailable = detail !== null && isSourceUnavailable(detail);
  // The server's word on this caller and this TC — not where it came from,
  // and not whether its origin can be read (that is `sourceUnavailable`).
  const editGate = detail ? testCaseGate(detail, "edit") : null;
  // A dropped TC can still be retired, but never re-activated (BE answers
  // 409 TC_DROPPED) — a drop is for good, whatever edit allows.
  const toggleGate =
    detail && isDropped(detail) && !detail.is_active
      ? { allowed: false as const, reason: reactivateDroppedReason(detail.drop_reason_code) }
      : editGate;

  const toggleActive = async () => {
    if (!detail || saving) return;
    setSaving(true);
    setError("");
    try {
      const next = await source.setActive(detail.test_case_id, !detail.is_active);
      setDetail(next);
      onUpdated(next);
    } catch (err) {
      setError(testCaseErrorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    // `minmax(0,1fr)`, not the implicit `auto` column: an auto track grows
    // to its widest child, so one unbroken id in the header widened every
    // row below past the 400px panel and the card clipped them.
    <Card className="grid grid-cols-[minmax(0,1fr)] gap-0 overflow-hidden lg:sticky lg:top-5">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        {/* The id gives way first — in full in the 식별자 block below. */}
        <span className="min-w-0 truncate text-sm font-semibold" title={openId}>
          {openId}
        </span>
        {detail && (
          <>
            {/* Where the case came from — the one place a prod TC says so. */}
            <Badge size="compact" className="shrink-0">
              {detail.source_environment}
            </Badge>
            {isSourceUnavailable(detail) && (
              <Badge size="compact" className="shrink-0">
                {SOURCE_UNAVAILABLE_LABEL}
              </Badge>
            )}
            {isSourceDeleted(detail) && (
              <Badge size="compact" className="shrink-0" title={SOURCE_DELETED_TEXT}>
                {SOURCE_DELETED_LABEL}
              </Badge>
            )}
            {/* Dropped and retired are independent, so both are shown. */}
            {isDropped(detail) && (
              <Badge size="compact" className="shrink-0 text-destructive">
                {DROPPED_LABEL}
              </Badge>
            )}
            <Badge size="compact" variant={detail.is_active ? "accent" : "default"} className="shrink-0">
              {detail.is_active ? "활성" : "은퇴"}
            </Badge>
          </>
        )}
        <div className="ml-auto shrink-0">
          <IconButton onClick={onClose} label="닫기">
            <X />
          </IconButton>
        </div>
      </div>

      <div className="grid max-h-[calc(100vh-11rem)] grid-cols-[minmax(0,1fr)] gap-4 overflow-y-auto p-4">
        {error && <Banner tone="error">{error}</Banner>}

        {loading ? (
          <div className="grid gap-2">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : !detail ? (
          !error && <p className="text-sm text-muted-foreground">이 TC 를 찾을 수 없습니다.</p>
        ) : (
          <>
            {/* 1 — the replay input, said as such. Calling it "질문" would
                read as background rather than as the thing that gets re-run. */}
            {/* Not a Block: that wraps its text in a <p>, and the summary holds controls. */}
            {loopApi && (
              <div className="rounded-lg border border-border border-l-2 border-l-primary bg-surface-subtle p-3">
                <span className="mb-1 block text-[11px] tracking-widest text-muted-foreground uppercase">원인 루프</span>
                <CauseLoopSummary api={loopApi} testCaseId={detail.test_case_id} />
              </div>
            )}
            <Block label="Query — 재현의 입력">
              {String(detail.trigger_message.content ?? "") || "—"}
            </Block>

            {detail.replay_source_id && <p className="text-xs text-muted-foreground">고정된 실행 원본 · {detail.replay_source_id}</p>}
            <CriteriaBlock
              key={detail.test_case_id}
              detail={detail}
              source={source}
              gate={editGate}
              onRegenerated={refresh}
            />
            {/* 2 — the only sentence on this screen a person wrote, and a
                scoring input besides (BE §3.1). */}
            {detail.comment && (
              <Block label={detail.source_feedback_id ? "Feedback comment" : "검토한 문제"} tone="warn">
                {detail.comment}
              </Block>
            )}

            {/* 2b — every 👎 on this answer. Only the first made the TC; the
                rest are here to read and change nothing (TCFEEDBACK). */}
            {detail.answer_feedbacks !== undefined && (
              <AnswerFeedbacks feedbacks={detail.answer_feedbacks} />
            )}

            {/* 3 */}
            <Section title="분류">
              <Row label="피드백 항목">
                <Chips values={detail.categories} />
              </Row>
              {differs(detail.categories, detail.feedback_categories) && (
                <Row label="원본 👎 합집합">
                  <div className="grid gap-1">
                    <Chips values={detail.feedback_categories} />
                    <span className="text-[12px] text-muted-foreground">
                      승격 시점 스냅샷과 다릅니다 — 목록 필터는 스냅샷 기준입니다.
                    </span>
                  </div>
                </Row>
              )}
              <Row label="도메인">
                {detail.domain ? (
                  <div className="grid gap-1">
                    <span className="text-[13px]">{domainLabel(detail.domain)}</span>
                    {/* The slug is what BE stores and what a link filters
                        on; the label above is only for reading. */}
                    <span className="text-[12px] text-muted-foreground">
                      {detail.domain}
                      {isProposedDomain(detail.domain) &&
                        " · AI 제안 — rubric 에 기준이 없어 Category 채점에서 빠집니다"}
                    </span>
                  </div>
                ) : (
                  <span className="text-[13px] text-muted-foreground">
                    미분류 — Category 채점에서 빠집니다 (rubric v2 R9.2)
                  </span>
                )}
              </Row>
            </Section>

            {/* 4 */}
            <Section title="케이스 정보">
              <Row label="세션 유형">
                <span className="text-[13px]">
                  {detail.session_type ? sessionTypeLabel(detail.session_type) : "—"}
                </span>
              </Row>
              <Row label="실행 주체">
                <span className="text-[13px] break-all">{detail.trigger_sender_email ?? "—"}</span>
                <p className="mt-0.5 text-[12px] text-muted-foreground">
                  👎 를 누른 사람이 아니라, 프롬프트가 조립된 기준이 된 발신자입니다.
                </p>
              </Row>
              <Row label="trigger 해석">
                <span className="text-[13px]">{detail.trigger_resolution ?? "—"}</span>
                {detail.trigger_resolution === "inferred" && (
                  <p className="mt-0.5 text-[12px] text-warning-foreground">
                    추정으로 잡힌 트리거라 재현이 원본과 다를 수 있습니다.
                  </p>
                )}
              </Row>
              <Row label="연결된 👎">
                <span className="tabular-nums">{feedbackCountText(detail.feedback_count)}</span>
                <span className="text-muted-foreground"> · 한 응답당 TC 는 1 건</span>
                {detail.authors.length > 0 && (
                  <p className="mt-0.5 text-[12px] break-all text-muted-foreground">
                    {detail.authors.join(" · ")}
                  </p>
                )}
                {/* Unread is not zero: an empty 👎 list here means nobody
                    could look, not that the 👎 were removed. */}
                {sourceUnavailable && (
                  <p className="mt-0.5 text-[12px] text-muted-foreground">
                    {sourceUnavailableText(detail.source_load_error)}
                  </p>
                )}
              </Row>
              <Row label="증거 스냅샷">
                {detail.evidence_status === "present" ? (
                  <>
                    <span className="text-[13px] tabular-nums">
                      message ×{detail.evidence_message_count} · file ×{detail.evidence_file_count}
                    </span>
                    {sourceUnavailable && (
                      <p className="mt-0.5 text-[12px] text-muted-foreground">
                        이 TC 에 보관된 증거라 원본을 확인할 수 없어도 그대로 볼 수 있습니다.
                      </p>
                    )}
                  </>
                ) : detail.evidence_status === "unknown" ? (
                  <span className="text-[13px] text-muted-foreground">
                    확인 불가 — 원본을 불러오지 못해 증거 여부를 알 수 없습니다.
                  </span>
                ) : (
                  <span className="text-[13px] text-destructive">evidence_missing</span>
                )}
              </Row>
              {isDropped(detail) && (
                <Row label="탈락">
                  <span className="text-[13px] text-destructive">
                    {droppedReasonText(detail.drop_reason_code)}
                  </span>
                  <p className="mt-0.5 text-[12px] text-muted-foreground">
                    {detail.dropped_at ? `${compactTime(detail.dropped_at)} · ` : ""}
                    TC 와 지난 실행 결과는 그대로 보존됩니다.
                  </p>
                </Row>
              )}
              {detail.warnings.length > 0 && (
                <Row label="경고">
                  <ul className="grid gap-0.5">
                    {detail.warnings.map((warning) => (
                      <li key={warning} className="text-[12px]">
                        {warningText(warning)}
                      </li>
                    ))}
                  </ul>
                </Row>
              )}
            </Section>

            {/* 5 — the only way to line this row up with a BE log, so it is
                not abbreviated. */}
            {/* One grid for the whole block rather than one per row. `auto`
                sizes the label column to the longest label there actually
                is, so it can never spill into the value the way the fixed
                104px column did — and because all four rows share that one
                column, the values still line up with each other. */}
            {originCandidates && (
              <OriginCandidates testCaseId={detail.test_case_id} load={originCandidates} />
            )}

            <Section title="식별자">
              <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2.5 gap-y-2">
                <IdRow label="test_case_id">{detail.test_case_id}</IdRow>
                <IdRow label="assistant_message_id">
                  {detail.assistant_message_id}
                </IdRow>
                <IdRow label="source_session_id">
                  {detail.source_session_id ?? "—"}
                </IdRow>
                <IdRow label="승격">
                  {compactTime(detail.promoted_at)} · {detail.promoted_by ?? "—"}
                </IdRow>
              </div>
            </Section>

            {/* 6.7 — the original run only, 0 or 1. Most seeded TCs have
                none, and that costs nothing but the before/after comparison;
                re-runs land in Batch Test, not here. */}
            <Section title="TE 실행 이력">
              {detail.executions.length === 0 ? (
                <p className="text-[13px] text-muted-foreground">
                  원본 실행 기록이 없습니다. 재실행 결과는 Batch Test 의 결과에 쌓입니다.
                </p>
              ) : (
                <div className="grid gap-2">
                  {detail.executions.map((execution) => (
                    <div
                      key={execution.execution_id}
                      className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-2"
                    >
                      <span className="text-[13px]">{compactTime(execution.executed_at ?? execution.created_at)}</span>
                      <Badge size="compact">{execution.replay_mode ?? "원본 실행"}</Badge>
                      {execution.commit_sha && (
                        <span className="text-[12px] text-muted-foreground">
                          {execution.commit_sha.slice(0, 7)}
                        </span>
                      )}
                      <span className="ml-auto">
                        <EvaluationStatusBadge outcome={execution.evaluation} size="compact" />
                      </span>
                      {execution.criteria_comparable === false && (
                        <span className="basis-full text-[12px] text-warning-foreground">
                          {incomparableText(execution)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* 6.6 — this TC's results first, then where it came from. The
                Matrix belongs to Test Result, so that is where its re-runs
                are read; Batch Test is where they are started. */}
            <div className="flex flex-wrap gap-2 border-t border-border pt-3">
              <Button size="sm" variant="outline" onClick={() => onOpenResult(detail)}>
                Test Result 에서 보기
              </Button>
              {/* Conversations looks the trace up by session, in a database
                  the TC names; without both there is nothing to look up. */}
              <Button
                size="sm"
                variant="outline"
                onClick={() => onOpenOriginal(detail)}
                disabled={!detail.source_session_id || !traceEnvironment(detail)}
                title={
                  !detail.source_session_id
                    ? "원본 세션 id 가 없어 대화를 찾을 수 없습니다"
                    : !traceEnvironment(detail)
                      ? "원본 환경이 dev · prod 중 무엇인지 알 수 없어 대화를 찾을 수 없습니다"
                      : undefined
                }
              >
                원본 대화 Trace
              </Button>
              {/* Retiring drops a TC out of future batches and keeps its past
                  results. It is never a delete (BE §13.5). */}
              <Button
                size="sm"
                variant="outline"
                onClick={() => void toggleActive()}
                // BE refuses an edit it does not allow (e.g.
                // PROD_TEST_CASE_READ_ONLY) — say so before the click
                // instead of after a 409.
                disabled={saving || !toggleGate?.allowed}
                title={toggleGate?.reason ?? undefined}
              >
                {saving && <Loader2 className="animate-spin" />}
                {detail.is_active ? "은퇴시키기" : "다시 활성화"}
              </Button>
              {toggleGate && !toggleGate.allowed && (
                <p className="basis-full text-[12px] text-muted-foreground">{toggleGate.reason}</p>
              )}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

function Block({
  label,
  tone,
  children,
}: {
  label: string;
  tone?: "warn";
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-lg border border-border bg-surface-subtle p-3 ${
        tone === "warn" ? "border-l-2 border-l-warning" : "border-l-2 border-l-primary"
      }`}
    >
      <span className="mb-1 block text-[11px] tracking-widest text-muted-foreground uppercase">
        {label}
      </span>
      <p className="text-[14px] whitespace-pre-wrap">{children}</p>
    </div>
  );
}

/**
 * The uppercase + letter-spacing is what makes a label read as a label —
 * not the monospace face it used to carry. Dropping `font-mono` here puts
 * the whole panel on Pretendard without flattening the hierarchy.
 */
/**
 * The way back to Issue Candidates. Empty for TCs that never went through the
 * pipeline (the older 👎 auto-promotion), which is said rather than hidden.
 */
function OriginCandidates({
  testCaseId,
  load,
}: {
  testCaseId: string;
  load: (testCaseId: string) => Promise<Candidate[]>;
}) {
  const [items, setItems] = useState<Candidate[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError("");
    load(testCaseId)
      .then((next) => !cancelled && setItems(next))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)));
    return () => {
      cancelled = true;
    };
  }, [testCaseId, load]);
  return (
    <Section title="출신 후보">
      {error ? (
        <p className="text-[13px] text-muted-foreground">후보를 불러오지 못했습니다 · {error}</p>
      ) : items === null ? (
        <Skeleton className="h-10" />
      ) : items.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">
          이슈 후보 파이프라인을 거치지 않은 TC입니다.
        </p>
      ) : (
        <ul className="grid gap-2">
          {items.map((item) => (
            <li key={item.candidate_id} className="grid gap-1 rounded-md border border-border p-2.5">
              <span className="flex flex-wrap items-center gap-1.5">
                <Badge size="compact" variant={item.status === "promoted" ? "accent" : "default"}>
                  {item.status === "promoted"
                    ? item.pipeline_stage === "second" ? "이전 방식(2차)에서 승격" : "1차 후보에서 승격"
                    : "기존 TC로 연결"}
                </Badge>
                <span className="text-[12px] text-muted-foreground">{item.problem_type}</span>
              </span>
              <span className="line-clamp-2 text-[13px]">{item.description}</span>
              <a
                className="text-[12px] text-link hover:underline"
                href={`/test-cases/candidates?candidate=${encodeURIComponent(item.candidate_id)}`}
              >
                후보 열기 →
              </a>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

export const TC_SOURCE_FEEDBACK_LABEL = "TC를 만든 👎";

function AnswerFeedbacks({ feedbacks }: { feedbacks: TestCaseAnswerFeedback[] | null }) {
  return (
    <Section title={feedbacks === null ? "이 답의 👎" : `이 답의 👎 (${feedbacks.length})`}>
      <p className="text-[12px] text-muted-foreground">
        이 목록은 보기 전용이며 TC와 저장된 기준을 바꾸지 않습니다.
      </p>
      {feedbacks === null ? (
        <p className="text-[13px] text-muted-foreground">지금은 이 답의 👎를 확인할 수 없습니다.</p>
      ) : feedbacks.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">받아 둔 👎가 없습니다.</p>
      ) : (
        <ul className="grid gap-2">
          {feedbacks.map((feedback) => (
            <li
              key={feedback.feedback_id}
              className="grid gap-1 rounded-md border border-border px-3 py-2"
            >
              <span className="flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
                {feedback.is_tc_source && (
                  <Badge size="compact" variant="accent">
                    {TC_SOURCE_FEEDBACK_LABEL}
                  </Badge>
                )}
                <span className="tabular-nums">{compactTime(feedback.created_at)}</span>
                {feedback.author_email && <span className="break-all">· {feedback.author_email}</span>}
                {/* Withheld by the server, as the 👎 list would withhold it (VERIFY-84). */}
                {feedback.author_hidden && <span>· 작성자는 prod 권한이 있어야 볼 수 있습니다</span>}
                {/* Which version came last is not known (receipt order is not commit order). */}
                {feedback.revised && <span>· 수정된 적 있음 · 순서 확인 불가</span>}
              </span>
              <Chips values={feedback.categories} />
              {feedback.comment ? (
                <p className="text-[13px] whitespace-pre-wrap">{feedback.comment}</p>
              ) : (
                <p className="text-[12px] text-muted-foreground">코멘트 없음</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-2">
      <h2 className="text-[11px] tracking-widest text-muted-foreground uppercase">
        {title}
      </h2>
      <div className="grid gap-2">{children}</div>
    </section>
  );
}

/** Two columns. For short labels — anything that fits 104px. */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[104px_minmax(0,1fr)] items-start gap-3">
      <span className="pt-0.5 text-[11px] tracking-wide text-muted-foreground uppercase">
        {label}
      </span>
      <div className="min-w-0 text-[14px]">{children}</div>
    </div>
  );
}

/**
 * A label/value pair for the identifier block. Returns two grid children
 * instead of wrapping them — that is what lets all four rows share the
 * parent's one auto-sized label column.
 *
 * Two departures from `Row`, both to buy width in a 400px drawer so the id
 * fits beside its label rather than under it:
 *
 * - No `uppercase`/`tracking`. These labels are literal column names, so
 *   lowercase snake_case is both what the schema calls them and about 20%
 *   narrower than the shouted version.
 * - 12px, not the 13px the rest of the panel's values use. Still up from
 *   the 11px this started at.
 *
 * `whitespace-nowrap` on the label makes the `auto` column resolve to the
 * full label width; `min-w-0` + `break-all` lets the value use whatever is
 * left and wrap inside its own column if a value ever runs longer.
 */
function IdRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <span className="pt-px text-[11px] whitespace-nowrap text-muted-foreground">
        {label}
      </span>
      <span className="min-w-0 text-[12px] break-all">{children}</span>
    </>
  );
}

function Chips({ values }: { values: readonly string[] }) {
  if (values.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {values.map((value) => (
        <Badge key={value} size="compact">
          {value}
        </Badge>
      ))}
    </span>
  );
}

function differs(a: readonly string[], b: readonly string[]) {
  if (a.length !== b.length) return true;
  const set = new Set(a);
  return b.some((value) => !set.has(value));
}

/** A score made against other criteria than the ones in use now (TCFEEDBACK B8). */
export function incomparableText(execution: Pick<TestExecutionSummary, "criteria_version">) {
  return typeof execution.criteria_version === "number"
    ? `기준 v${execution.criteria_version} 점수 · 현재 기준과 비교 불가`
    : "기준 버전 미확인 · 현재 기준과 비교 불가";
}

/** Each warning implies a different next step, so none of them collapse. */
function warningText(warning: TestCaseWarning) {
  switch (warning) {
    case "trigger_inferred":
      return "트리거가 추정값 — 재현이 원본과 다를 수 있습니다.";
    case "evidence_missing":
      return "증거 스냅샷이 없습니다 — 재실행할 수 없습니다.";
    case "source_unavailable":
      return "원본 기록을 지금 확인할 수 없어 삭제 여부를 알 수 없습니다. 이 TC 에 보관된 증거와 실행 결과는 그대로 볼 수 있습니다.";
    case "source_feedback_deleted":
      return SOURCE_DELETED_TEXT;
    case "no_execution":
      return "원본 실행 기록(TE)이 없습니다 — 재실행·채점에는 지장이 없고, 원본 대비 비교만 할 수 없습니다.";
  }
}
